import { FC, useCallback } from "react";
import { useSelector } from "react-redux";
import { EmptyMessage } from "src/components";
import { PdTableCellText } from "src/components/layout/table";
import { useFocusableQuery } from "src/hooks/api";
import { tColumn } from "src/hooks/table";
import api from "src/util/apiNew";
import { tSalesRepReport } from "tmt_api/src/types/dtos";

import { PdSpinner, ReportTable } from "./components";
import { formatDate, getParams } from "./utils";

const getSalesRepsReport = (start: Date, end: Date) =>
  api.get<tSalesRepReport[]>("reports/sales-reps", getParams(start, end)).then((res) => res.data);

const columns: tColumn<tSalesRepReport>[] = [
  {
    id: "dateJoinedInSeconds",
    header: "Date of Application",
    accessor: (originalRow) => formatDate(originalRow.dateJoinedInSeconds),
    Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
    style: { width: 160 },
  },
  {
    id: "salesRepName",
    header: "Sales Rep Name",
    accessor: (originalRow) => originalRow.salesRepName,
    Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
    style: { width: 200 },
  },
  {
    id: "email",
    header: "Sales Rep Email",
    accessor: (originalRow) => originalRow.email,
    Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
    style: { width: 220 },
  },
  {
    id: "phoneNumber",
    header: "Sales Rep Phone Number",
    accessor: (originalRow) => originalRow.phoneNumber || "-",
    Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
    style: { width: 200 },
  },
  {
    id: "brandName",
    header: "Sales Rep Brands",
    accessor: (originalRow) => originalRow.brandName || "-",
    Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
    style: { width: 200 },
  },
  {
    id: "retailer",
    header: "Sales Rep Retailers",
    accessor: (originalRow) => originalRow.retailer || "-",
    Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
    style: { width: 200 },
  },
  {
    id: "type",
    header: "Sales Rep Type",
    accessor: (originalRow) => originalRow.type,
    Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
    style: { width: 200 },
  },
  {
    id: "averagePrice",
    header: "Average MSRP",
    accessor: (originalRow) => originalRow.averagePrice || "-",
    Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
    style: { width: 200 },
  },
  {
    id: "brandQuantity",
    header: "How many brands do you current represent?",
    accessor: (originalRow) => originalRow.brandQuantity || "-",
    Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
    style: { width: 200 },
  },
  {
    id: "brandCategories",
    header: "What Categories best describe your brands?",
    accessor: (originalRow) =>
      (originalRow.brandCategories?.length ?? 0) > 0 ? originalRow.brandCategories?.join(", ") : "-",
    Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
    style: { width: 200 },
  },
];

const SalesRepsTable: FC<{ period: Date[] }> = ({ period }) => {
  const config = useSelector((state) => state.TableConfigReducer.salesReps);
  const { isLoading, data } = useFocusableQuery<tSalesRepReport[]>(
    useCallback(() => getSalesRepsReport(period[0], period[1]), [period]),
    { enabled: !!(period[0] && period[1]) },
  );

  if (isLoading) {
    return <PdSpinner />;
  }

  if (!data || data?.length === 0) {
    return <EmptyMessage message="There is no data to display" />;
  }

  return (
    <ReportTable
      data={data}
      columns={columns}
      keyExtractor={(item) => item.id}
      sortedBy="dateJoinedInSeconds"
      columnsConfig={config}
      tableConfigReduxKey="salesReps"
    />
  );
};

export default SalesRepsTable;
